import React, { useCallback, useState, useRef, memo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@material-ui/core/styles'
import { nanoid } from 'nanoid'
import { useSnackbar } from 'notistack'

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
} from '@material-ui/core'

import messages from '../../../services/messages'
import { isFunction, find } from '../../../utils/customMethods'

import {
  ErrorOutline as ErrorIcon,
  CloseRounded as CloseIcon,
  MoodRounded as MoodIcon,
  SendRounded as SendIcon,
  AttachmentRounded as AttachmentIcon,
  AddCircleRounded as AddIcon,
  ArrowForwardIosRounded as NextIcon,
  ArrowBackIosRounded as BackIcon,
} from '@material-ui/icons'

import Typography from '../../atoms/display/typography'
import Icon from '../../atoms/display/icon'
import IconButton from '../../atoms/inputs/iconButton'
import TextField from '../../atoms/inputs/textfield'
import Emojis from '../../molecules/display/emojis'

import useStyles from './uploadStyle'

const Upload = (props) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  const inputLegendRef = useRef()
  const selectButtonRef = useRef()
  const [zoom, setZoom] = useState(1)
  const [rotate, setRotate] = useState(0)
  const [picture, setPicture] = useState(0)
  const [files, setFiles] = useState([])
  const [uploadedFiles, setUploadedFiles] = useState({})
  const [loading, setLoading] = useState(false)
  const [chatId, setChatId] = useState()
  const [showEmojis, setShowEmojis] = useState(false)
  const [message, setMessage] = useState('');

  const handleEmojiOnClick = (emoji, event) => {
    inputLegendRef.current.value =
      inputLegendRef.current.value + ' ' + emoji.native
  }

  useEffect(() => {
    if(props?.predefinedFile){
      createFile(props?.predefinedFile?.files)
      setMessage(props?.predefinedFile?.message)
    } else if(!props?.predefinedFileDrop?.length){
      selectButtonRef?.current?.click()
    }
  }, [props?.predefinedFile])

  useEffect(() => {
    if(props?.predefinedFileDrop){
      props?.predefinedFileDrop?.map( async (e) => {
        const response = await fetch(e.url);
        const data = await response.blob();
        let file = new File([data], e.name, {
          type: e.type,
        });
        handlePredefinedFiles(file);
      })
    }
  }, [props?.predefinedFileDrop])

  useEffect(() => props?.chatId && setChatId(props.chatId), [props?.chatId])

  const fileValidate = (file) => {
    let typeExtension = file?.file?.type.split('/');
    let type = typeExtension[0] === 'application' ? 'file' : typeExtension[0]
    const extensions = file?.file?.name.split('.').pop().toLowerCase();
    const size = file?.file?.size;
      //Validate FileType
      if (
        type === "image" ||
        type === "file" ||
        type === "audio" ||
        type === "video"
      ) {
        //Validate FileExt
        if (
          props?.supportedFiles[type]?.ext.find((el) => el === extensions)
        ) {
          const maxSizeToBytes =
            props?.supportedFiles[type]?.metric === "KB"
              ? props?.supportedFiles[type]?.maxSize * 1024
              : props?.supportedFiles[type]?.metric === "MB"
              ? props?.supportedFiles[type]?.maxSize * 1024 * 1024
              : props?.supportedFiles[type]?.metric === "GB"
              ? props?.supportedFiles[type]?.maxSize * 1024 * 1024 * 1024
              : props?.supportedFiles[type]?.metric === "TB"
              ? props?.supportedFiles[type]?.maxSize * 1024 * 1024 * 1024 * 1024
              : props?.supportedFiles[type]?.maxSize;
          //Validate FileSize
          if (size < maxSizeToBytes) {
            return { error: false, message: null };
          } else {
            return {
              error: true,
              message: `${t('commons.errors.AddFilesSmallerThan')} ${
                props?.supportedFiles[type]?.maxSize
              } ${props?.supportedFiles[type]?.metric}`,
            };
          }
        } else {
          const msgExt = `${t('commons.errors.FileExtensionNotSupportedInThisChannel')}`;
          return {
            error: true,
            message: msgExt
          }
        }
      } else {
        return {
          error: true,
          message: t("commons.errors.FileNotValid"),
        };
      }
  };

  const createFile = async(url) => {
    const response = await fetch(url);
    const data = await response.blob();
    const matchUrl = url.match(new RegExp('name.' + "(.*)" + "sv"));
    const name = matchUrl[1].replace('?', '');
    const metadata = {
      type: data.type
    }
    const file = new File([data], name, metadata);
    handlePredefinedFiles(file) 
  }

  const handlePredefinedFiles = useCallback((file) => {
    const id = nanoid()
    const blobURL = URL.createObjectURL(file)
    const preview = new Image()
    const isImage = file?.type?.split('/')?.[0] === 'image'
    preview.src = isImage ? blobURL : 'file-thumbnail.png'
    preview.onerror = (error) => {
      URL.revokeObjectURL(blobURL)
    }
    preview.onload = () => {
      setFiles((files) => [
        ...files,
        {
          id: id,
          preview: preview.src,
          file: file,
          name: !isImage && file?.name,
        },
      ])

      setUploadedFiles((prev) => ({
        ...prev,
        [id]: {
          preview: preview.src,
          status: { loading: false, success: false, error: false },
        },
      }))
    }
  }, [])

  const updateStatus = (id, newStatus) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        status: { loading: false, success: false, error: false, ...newStatus },
      },
    }))
  }

  const uploadFile = async (file) => {
    if (file) {
      updateStatus(file.id, { loading: true })
      setPicture(files.indexOf(file))
      try {
        const sendTime = new Date().getTime()
        const formData = new FormData()
        formData.append(file.id, file.file)

        const response = await messages.post(
          `/channel/upload/file/${chatId}/${sendTime}/`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        )

        if (response.status === 200) {
          uploadedFiles[file.id].fileId = response.data.data?.fileId
          uploadedFiles[file.id].status.success = true
          updateStatus(file.id, { success: true })
        } else {
          throw response.data
        }
      } catch (error) {
        const message = error?.response?.data?.message?.[0]?.value
        const messageTranslated = t(`commons.errors.${message}`)
        if (messageTranslated.startsWith('commons.errors')) {
          enqueueSnackbar(t(`commons.errors.theFileCouldNotBeSent`), {
            variant: 'error',
          })
        } else {
          enqueueSnackbar(t(`commons.errors.${message}`), {
            variant: 'error',
          })
        }
        uploadedFiles[file.id].status.error = true
        updateStatus(file.id, { error: true })
      }
    }
  }

  const handleSelectedFiles = useCallback((event) => {
    let numFiles = event.target.files.length
    
    for (let x = 0; x < numFiles; x++) {
      const id = nanoid()
      const blobURL = URL.createObjectURL(event.target.files[x])
      const preview = new Image()
      const isImage = event?.target?.files?.[x]?.type?.split('/')?.[0] === 'image'
      preview.src = isImage ? blobURL : 'file-thumbnail.png'
      preview.onerror = (error) => {
        URL.revokeObjectURL(blobURL)
      }
      preview.onload = () => {
        // URL.revokeObjectURL(blobURL)
        setFiles((files) => [
          ...files,
          {
            id: id,
            preview: preview.src,
            file: event.target.files[x],
            name: !isImage && event.target.files[x].name,
          },
        ])

        setUploadedFiles((prev) => ({
          ...prev,
          [id]: {
            preview: preview.src,
            status: { loading: false, success: false, error: false },
          },
        }))
      }
    }
  }, [])

  const handleUpload = async () => {
   try {
    if (files.length > 0) {
      setLoading(true)
      for (const file of files) {
        let isValid = fileValidate(file)
        if(!isValid.error){
          if (
            !(
              uploadedFiles[file.id]?.status?.success ||
              uploadedFiles[file.id]?.status?.error
            )
          )
            await uploadFile(file)
        } else{
          throw new Error(isValid.message)
        }
      }
    }
   } catch (error) {
      const messageError = error.message
      enqueueSnackbar(messageError, {
        variant: 'error',
      })
   } finally {
    setLoading(false)
   }
  }

  const handleSubmit = async () => {
    const legend = inputLegendRef.current.value
    const list = Object.values(uploadedFiles)

    await handleUpload()

    if (legend) {
      const condition = (e) => e.status.success && true
      const lastUploaded = find(list, condition, true)
      if (lastUploaded) lastUploaded.text = legend
    }
    if (list.some((e) => e.status.success)) {
      isFunction(props?.onSubmit) &&
        props.onSubmit(Object.values(uploadedFiles))
      isFunction(props?.setOpen) && props.setOpen(false)
    }
  }

  const renderPicture = () => {
    if (files[picture]?.preview) {
      return (
        <div className={classes.pictures}>
          <img
            className={classes.picture}
            src={files[picture]?.preview}
            alt=""
          />
          {files[picture]?.name && (
            <Typography
              variant="body1"
              style={{ marginTop: 5, wordBreak: 'break-all' }}
            >
              {files[picture]?.name}
            </Typography>
          )}
        </div>
      )
    } else {
      return (
        <>
          <div className={classes.emptySpace}>
            <Icon>
              <AttachmentIcon />
            </Icon>
            <Typography
              variant="body2"
              style={{
                color: theme.palette.highlightSearchActive,
                marginTop: 10,
              }}
            >
              {t('commons.emptyAttachment')}
            </Typography>
          </div>
        </>
      )
    }
  }

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="md"
        open={props.open}
        onClose={() => props.setOpen(false)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle id="form-dialog-title">
          <div
            style={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>{t('commons.sendAttachments')}</div>
            <div>
              <IconButton
                onClick={() => props.setOpen(false)}
                style={{ color: theme.palette.primary.main }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </DialogTitle>
        <DialogContent>
          <div className={classes.content}>
            <IconButton
              color="primary"
              onClick={() => {
                picture > 0
                  ? setPicture(picture - 1)
                  : setPicture(files.length - 1)
              }}
            >
              <BackIcon />
            </IconButton>
            <div className={classes.stage}>{renderPicture()}</div>
            <IconButton
              color="primary"
              onClick={() => {
                picture < files.length - 1
                  ? setPicture(picture + 1)
                  : setPicture(0)
              }}
            >
              <NextIcon />
            </IconButton>
          </div>
          {showEmojis && (
            <div className={classes.emojiPanel}>
              <Emojis open={setShowEmojis} onClick={handleEmojiOnClick} />
            </div>
          )}
        </DialogContent>
        <div className={classes.renderBar}>
          <TextField
            label={t('fields.message.label')}
            placeholder={t('fields.message.placeHolder')}
            type="text"
            color="primary"
            variant="outlined"
            name="message"
            inputRef={inputLegendRef}
            shrink={true}
            fullWidth={true}
            onChange={(e) => {
              setMessage(e.target.value)
            }}
            value={message}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'nowrap',
              marginLeft: 10,
            }}
          >
            <IconButton
              color="primary"
              onClick={() => setShowEmojis(showEmojis ? false : true)}
            >
              <MoodIcon style={{ color: theme.palette.primary.main }} />
            </IconButton>
            <div style={{ width: 10 }} />
            <Button
              size="large"
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={25} color="primary" />
              ) : (
                t('buttons.send')
              )}
            </Button>
          </div>
        </div>
        <div className={classes.footer}>
          <div className={classes.spacer} />
          <label htmlFor="contained-button-file">
            <div className={classes.selectButton}>
              <AddIcon
                style={{ color: theme.palette.primary.main, fontSize: 32 }}
              />
              <Typography
                variant="body2"
                style={{ color: theme.palette.primary.main }}
              >
                {t('buttons.add')}
              </Typography>
            </div>
          </label>
          <div className={classes.spacer} />
          {files.length > 0 &&
            files.map((el, index) => {
              return (
                <>
                  <div className={classes.thumbAlign}>
                    <div
                      className={`
                    ${
                      (uploadedFiles[el.id]?.status?.loading ||
                        uploadedFiles[el.id]?.status?.error) &&
                      classes.initial
                    }
                    ${
                      picture === index
                        ? classes.thumbnailActive
                        : classes.thumbnail
                    }
                    `}
                      style={{
                        backgroundImage: `url(${el.preview})`,
                        backgroundSize: 'cover',
                      }}
                      onClick={() => setPicture(index)}
                    ></div>
                    <div className={classes.iconContent}>
                      {uploadedFiles[el.id]?.status?.loading ? (
                        <CircularProgress size={45} color="light" />
                      ) : uploadedFiles[el.id]?.status?.error ? (
                        <ErrorIcon
                          color="light"
                          className={classes.icon}
                          onClick={() => setPicture(index)}
                        />
                      ) : (
                        ''
                      )}
                    </div>
                  </div>
                  <div className={classes.spacer} />
                </>
              )
            })}
        </div>
      </Dialog>
      <input
        ref={selectButtonRef}
        multiple
        accept={props?.acceptFiles}
        type="file"
        id="contained-button-file"
        style={{ display: 'none' }}
        onChange={handleSelectedFiles}
      />
    </>
  )
}

export default memo(Upload)
